// src/engine/ZobristHash.ts
// Zobrist hashing for chess position fingerprinting
// Each position gets a unique 64-bit hash that can be incrementally updated

import { Board, Piece, Position, PieceColor } from "../types/Board.ts";
import { getPieceAt } from "../utils/boardUtils.ts";

/**
 * Zobrist Hashing System
 * 
 * Creates unique 64-bit fingerprints for chess positions that can be
 * incrementally updated as moves are made/unmade. This is critical for
 * the transposition table to identify previously-seen positions.
 * 
 * Key properties:
 * - Same position always produces same hash
 * - XOR operation is reversible (piece ^ piece = 0)
 * - Can update hash incrementally without full recalculation
 */
export class ZobristHash {
  // Hash values for each [pieceType][square] combination
  private pieceSquareKeys: Map<string, bigint[]>;
  
  // Hash values for castling rights (4 flags per color, multiple colors)
  private castlingKeys: Map<string, bigint>;
  
  // Hash values for en passant file (one per file)
  private enPassantKeys: bigint[];
  
  // Hash value for side to move (just need one for "black to move")
  private sideToMoveKey: bigint;
  
  private boardSize: number;
  private numFiles: number;

  constructor(boardSize: number, numFiles: number) {
    this.boardSize = boardSize;
    this.numFiles = numFiles;
    this.pieceSquareKeys = new Map();
    this.castlingKeys = new Map();
    this.enPassantKeys = [];
    this.sideToMoveKey = 0n;
    
    this.initializeKeys();
  }

  /**
   * Initialize all random Zobrist keys
   */
  private initializeKeys(): void {
    // Initialize piece-square keys for all possible [pieceType][square] combinations
    // We'll initialize them lazily as we encounter new piece types
    
    // Initialize en passant keys (one per file)
    for (let file = 0; file < this.numFiles; file++) {
      this.enPassantKeys.push(this.random64());
    }
    
    // Initialize side to move key (black to move)
    this.sideToMoveKey = this.random64();
  }

  /**
   * Generate a pseudo-random 64-bit integer
   * JavaScript's Math.random() only has 53-bit precision, so we combine two values
   */
  private random64(): bigint {
    const high = BigInt(Math.floor(Math.random() * 0x100000000));
    const low = BigInt(Math.floor(Math.random() * 0x100000000));
    return (high << 32n) | low;
  }

  /**
   * Get or create Zobrist key for a piece at a square
   */
  private getPieceSquareKey(piece: Piece, square: number): bigint {
    // Create unique key for this piece type + color combination
    const pieceKey = `${piece.type.toLowerCase()}_${piece.color}`;
    
    if (!this.pieceSquareKeys.has(pieceKey)) {
      // Initialize keys for this piece type
      const keys: bigint[] = [];
      for (let i = 0; i < this.boardSize * this.boardSize; i++) {
        keys.push(this.random64());
      }
      this.pieceSquareKeys.set(pieceKey, keys);
    }
    
    return this.pieceSquareKeys.get(pieceKey)![square];
  }

  /**
   * Convert 2D position to 1D square index
   */
  private positionToSquare(pos: Position): number {
    return pos.row * this.numFiles + pos.col;
  }

  /**
   * Get or create castling key for a specific color and side
   */
  private getCastlingKey(color: PieceColor, side: 'kingSide' | 'queenSide'): bigint {
    const key = `${color}_${side}`;
    
    if (!this.castlingKeys.has(key)) {
      this.castlingKeys.set(key, this.random64());
    }
    
    return this.castlingKeys.get(key)!;
  }

  /**
   * Compute initial hash for a board position from scratch
   */
  computeHash(board: Board, sideToMove: PieceColor, enPassantTarget?: Position): bigint {
    let hash = 0n;
    
    // XOR in all pieces on the board
    for (let row = 0; row < board.dimensions.rows; row++) {
      for (let col = 0; col < board.dimensions.cols; col++) {
        const piece = getPieceAt(board, { row, col });
        if (piece) {
          const square = this.positionToSquare({ row, col });
          hash ^= this.getPieceSquareKey(piece, square);
        }
      }
    }
    
    // XOR in en passant file if exists
    if (enPassantTarget) {
      const file = enPassantTarget.col;
      if (file >= 0 && file < this.enPassantKeys.length) {
        hash ^= this.enPassantKeys[file];
      }
    }
    
    // XOR in side to move (if black)
    if (sideToMove === 'black') {
      hash ^= this.sideToMoveKey;
    }
    
    // Note: We're not hashing castling rights here since they're not in the basic Board type
    // If your game supports castling, you'd add them here
    
    return hash;
  }

  /**
   * Update hash when a piece moves (for make/unmake move)
   * @param hash Current hash
   * @param piece Piece being moved
   * @param from Source square
   * @param to Destination square
   * @param capturedPiece Piece being captured (if any)
   * @returns Updated hash
   */
  updateHashForMove(
    hash: bigint,
    piece: Piece,
    from: Position,
    to: Position,
    capturedPiece?: Piece
  ): bigint {
    let newHash = hash;
    
    const fromSquare = this.positionToSquare(from);
    const toSquare = this.positionToSquare(to);
    
    // Remove piece from source square
    newHash ^= this.getPieceSquareKey(piece, fromSquare);
    
    // Remove captured piece if exists
    if (capturedPiece) {
      newHash ^= this.getPieceSquareKey(capturedPiece, toSquare);
    }
    
    // Add piece to destination square
    newHash ^= this.getPieceSquareKey(piece, toSquare);
    
    return newHash;
  }

  /**
   * Update hash when side to move changes
   */
  updateHashForSideChange(hash: bigint): bigint {
    return hash ^ this.sideToMoveKey;
  }

  /**
   * Update hash when en passant target changes
   */
  updateHashForEnPassant(
    hash: bigint,
    oldEnPassant: Position | undefined,
    newEnPassant: Position | undefined
  ): bigint {
    let newHash = hash;
    
    // Remove old en passant
    if (oldEnPassant && oldEnPassant.col < this.enPassantKeys.length) {
      newHash ^= this.enPassantKeys[oldEnPassant.col];
    }
    
    // Add new en passant
    if (newEnPassant && newEnPassant.col < this.enPassantKeys.length) {
      newHash ^= this.enPassantKeys[newEnPassant.col];
    }
    
    return newHash;
  }

  /**
   * Update hash for castling rights change
   * (Only needed if your game supports castling)
   */
  updateHashForCastling(
    hash: bigint,
    color: PieceColor,
    side: 'kingSide' | 'queenSide',
    hadRight: boolean,
    hasRight: boolean
  ): bigint {
    // If rights changed, XOR the castling key
    if (hadRight !== hasRight) {
      return hash ^ this.getCastlingKey(color, side);
    }
    return hash;
  }
}
